import { useQueryClient } from "@tanstack/react-query";
import {
  getFundFamilyEventRetrieveQueryKey,
  useFundFamilyEventDelete,
  useFundFamilyEventRefresh,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { FeatureFlagNameEnum } from "gen/model/featureFlagNameEnum";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "shared/components/alert/Alert";
import { FullScreenLoadingState } from "shared/components/empty/FullScreenLoadingState";
import { useToast } from "shared/components/toast/useToast";
import { useCeleryWorkflowPollSimple } from "shared/hooks/useCeleryWorkflowPoll";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { makeJsonItemSessionStorage } from "shared/storage/StorageUtils";

interface OutOfDateAlertProps {
  fundFamilyId: string;
  fundFamilyEventId: string;
  message: string;
  onDiscard: () => void;
  onRefresh?: () => void;
}

export const OutOfDateAlert: React.FC<OutOfDateAlertProps> = ({
  fundFamilyId,
  fundFamilyEventId,
  message,
  onDiscard,
  onRefresh,
}) => {
  const { t } = useTranslation(["common", "events"]);
  const { showToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const refreshEventActive = useFeatureFlagActive(
    FeatureFlagNameEnum.refresh_out_of_date_events,
  );
  const { mutateAsync: deleteFundFamilyEvent } = useFundFamilyEventDelete({
    mutation: {
      onSuccess: () => {
        onDiscard();
        showToast({ variant: "success", message: t("common:discarded") });
      },
    },
  });

  // We store the workflow ID of an actively ongoing refresh,
  // so if the user backs out and comes back, we still show the loading state correctly
  const ACTIVE_REFRESH_WORKFLOW_ID = `activeRefreshWorkflowId-${fundFamilyEventId}`;
  const activeRefreshWorkflowId = makeJsonItemSessionStorage<string>(
    ACTIVE_REFRESH_WORKFLOW_ID,
    "",
  );

  const [celeryWorkflowId, setCeleryWorkflowId] = useState(
    activeRefreshWorkflowId.get(),
  );

  const { areAnyProcessing } = useCeleryWorkflowPollSimple({
    celeryWorkflowIds: [celeryWorkflowId],
    forceOnSuccess: true,
    onSuccess: () => {
      showToast({ variant: "success", message: t("common:refreshed") });
      activeRefreshWorkflowId.set("");
      // In all cases we want to make sure fund family event is refreshed
      queryClient
        .invalidateQueries({
          queryKey: getFundFamilyEventRetrieveQueryKey(
            fundFamilyId,
            fundFamilyEventId,
          ),
        })
        // Additionally some pages may need to refresh extra info
        .then(() => {
          onRefresh?.();
        })
        .catch((err: unknown) => {
          showErrorToast(err);
        });
    },
    onError: (error: Error) => {
      activeRefreshWorkflowId.set("");
      setHasRefreshError(true);
      showErrorToast(error);
    },
  });

  const [hasRefreshError, setHasRefreshError] = useState(false);
  const { mutateAsync: refreshFundFamilyEvent } = useFundFamilyEventRefresh({
    mutation: {
      onSuccess: ({ celery_workflow_id }) => {
        setCeleryWorkflowId(celery_workflow_id);
        activeRefreshWorkflowId.set(celery_workflow_id);
      },
      onError: (error: Error) => {
        setHasRefreshError(true);
        showErrorToast(error);
      },
    },
  });

  const refreshEventAction = {
    title: t("common:refresh"),
    onClick: async () =>
      await refreshFundFamilyEvent({ fundFamilyId, fundFamilyEventId }),
  };

  const discardEventAction = {
    title: t("common:discard"),
    onClick: async () =>
      await deleteFundFamilyEvent({ fundFamilyId, fundFamilyEventId }),
  };

  return (
    <>
      {areAnyProcessing && (
        <FullScreenLoadingState message={t("events:refresh.loading")} />
      )}
      <Alert
        status={!refreshEventActive || hasRefreshError ? "error" : "warning"}
        mb="16px"
        description={
          <>
            {message}
            {refreshEventActive
              ? ` ${hasRefreshError ? t("events:outOfDateError") : t("events:outOfDateWarning")}`
              : ` ${t("events:outOfDateDiscard")}`}
          </>
        }
        primaryAction={
          refreshEventActive ? refreshEventAction : discardEventAction
        }
        secondaryAction={refreshEventActive ? discardEventAction : undefined}
      />
    </>
  );
};
